import { format, add, isWithinInterval, differenceInMinutes, fromUnixTime, isSameDay, parseISO, isAfter } from 'date-fns';
import { v4 } from 'uuid';
import store from '@/store';

import { verifyOverlap, sessionsPerContentType, sendNotification, sortArray, computeSessionMap, minutesToHours, isObjectEmpty, computeEffortAlert, buildDailySessionsQuery, separateSessionsByWeek } from './utils';
import { getSessionFieldByWtype, getOverlappedSessions } from './sessions';
import { openSearchCustomSessions } from './amplify';

function generateUuid () {
  return v4();
}

function busyContentTypes (wtype, session) {
  const sessionsInDay = store.getters.sessionByDate(session.start_time * 1000);
  const sessionsInterval = getOverlappedSessions(sessionsInDay, session);

  const busyContentType = [];

  sessionsInterval.map(s => busyContentType.push(...s[wtype]));

  if (wtype === 'horses') {
    sessionsInterval.map(s => s.instructors.map(i => busyContentType.push({ uuid: i.horse })));
  }

  return busyContentType;
}

const sessionTypesAllowInsertEffort = ['EVENEMENT', 'COMPETITION'];

function isContentTypeAvailable (contentType, session) {
  const { dailySessions } = store.getters;
  const sessionField = getSessionFieldByWtype(contentType.wtype);
  const sessiontypes = contentType.wtype === 'place' ? ['FERMETURE'] : ['ABSENCE', 'REPOS'];
  const contentTypeSessions = sessionsPerContentType(contentType, sessionField, dailySessions);

  const filteredSessionsByType = contentTypeSessions.filter(s => sessiontypes.includes(s.type));

  if (!filteredSessionsByType.length || !contentType.wtype) {
    return true;
  }

  return filteredSessionsByType.some(s => {
    const isBetween = isWithinInterval(new Date(session.start_time * 1000), { start: new Date(s.start_time * 1000), end: new Date(s.end_time * 1000) });

    return !isBetween;
  });
}

function availableContentType (array, sessionField, session, sessions, keepCurrentSession = true) {
  if (!session.end_time) {
    return [];
  }
  const filterArray = [...array];
  let indexToRemove = [];

  if (['instructors', 'horses'].includes(sessionField)) {
    const busyContent = busyContentTypes(sessionField, session);

    return filterArray.filter(c => !busyContent.some(contentInterval => contentInterval.uuid === c.uuid));
  }

  // remove unavailable horse -> horse that already have a session
  filterArray.forEach((contentType, index) => {
    const isOverlap = verifyOverlap(contentType, session, sessionField, sessions, keepCurrentSession);
    if (isOverlap) {
      indexToRemove.push(index);
    }

    const contentTypeAvailable = isContentTypeAvailable(contentType, session);

    if (!contentTypeAvailable) {
      indexToRemove.push(index);
    }

    return contentType;
  });

  indexToRemove = indexToRemove.sort((a, b) => (a < b ? -1 : 1));

  for (let i = indexToRemove.length - 1; i >= 0; i -= 1) {
    filterArray.splice(indexToRemove[i], 1);
  }

  return filterArray;
}

// check if horse is available based on entree/sortie
function isHorseAvailable (horse, horseAddListing) {
  if (horse.horse_resides_center) {
    return false;
  }
  const movements = horse.horse_movements && horse.horse_movements.length ? [...horse.horse_movements] : [];
  const lastestMovement = (movements || []).sort((a, b) => (a.horse_movement_date > b.horse_movement_date ? -1 : 1))[0];

  if (!lastestMovement || (lastestMovement && lastestMovement.horse_movement_type !== 'SORTIE')) {
    return true;
  }

  if (lastestMovement && lastestMovement.horse_movement_type === 'SORTIE' && horseAddListing) {
    return false;
  }

  const { selectedDate } = store.getters;

  const isSortieValid = lastestMovement.returnDate
    ? isWithinInterval(new Date(selectedDate), { start: fromUnixTime(lastestMovement.horse_movement_date), end: fromUnixTime(lastestMovement.returnDate) })
    : isAfter(new Date(selectedDate), fromUnixTime(lastestMovement.horse_movement_date));

  if (isSortieValid) {
    return false;
  }

  return true;
}

function computeEffort (sessions, contentType) {
  if (!sessions.length) {
    return 0;
  }

  let sessionsToCount = [...sessions];

  if (contentType && contentType.wtype === 'horse') {
    sessionsToCount = sessions.filter(s => !['SOINS', 'REPOS'].includes(s.type));
  }

  return sessionsToCount.reduce((accEffort, session) => {
    const diff = differenceInMinutes(fromUnixTime(session.end_time), fromUnixTime(session.start_time));

    let computedEffort = diff;

    const sessionHorse = session.horses.find(h => h.uuid === contentType.uuid);

    if (sessionHorse && sessionHorse.effort && sessionTypesAllowInsertEffort.includes(session.type)) {
      computedEffort = sessionHorse.effort * 60; // Conversion of hours to minutes.
    }

    if (session.type === 'GROUPE' && session.groupeHorseEffort) {
      return accEffort + (computedEffort * session.groupeHorseEffort);
    }

    if (session.discipline && !isObjectEmpty(session.discipline)) {
      const foundDiscipline = store.state.disciplines.find(discipline => discipline.uuid === session.discipline.uuid);
      if (foundDiscipline && foundDiscipline.weightingFactor) {
        return accEffort + (computedEffort * foundDiscipline.weightingFactor);
      }
    }
    return accEffort + computedEffort;
  }, 0);
}

function computeDailyWeeklyHorseEffort (horse) {
  const weeklySessions = store.getters.weeklySessionsByContentType(horse);
  const weeklyEffort = minutesToHours(computeEffort(weeklySessions, horse));
  const dailySessions = store.getters.dailySessionsByContentType(horse);
  const dailyEffort = minutesToHours(computeEffort(dailySessions, horse));
  return { ...horse, weeklyEffort, dailyEffort };
}

/**
 * available horses received 3 args
 * 1º - the session
 * 2º - keepCurrentSession (boolean  if we want to keep the current session content type) - this will remove the instructor horse too
 * 3º - removeInstructorHorse (boolean if we want to remove instructor horse)
 */
function availableHorses (session, keepCurrentSession = true, removeInstructorHorses = false, removeParticipantsHorses = false, sessions = store.state.sessions) {
  const { horses } = store.state;
  // get horse that is not on a session on the same time as the session reveiced
  let availablesHorses = availableContentType(horses, 'horses', session, sessions, keepCurrentSession);
  let indexToRemove = [];

  // remove horses that belong to customers
  availablesHorses = availablesHorses.filter(h => !h.owners || !h.owners.length);

  const instructorsHorsesUuid = (session.instructors || []).map(i => i?.horse);

  // remove instructor horse from available horses
  if (removeInstructorHorses && instructorsHorsesUuid.length) {
    instructorsHorsesUuid.map(uuid => {
      const index = availablesHorses.findIndex(horse => horse.uuid === uuid);
      if (index !== -1) {
        indexToRemove.push(index);
      }
    });
  }

  const participantsHorsesUuid = (session.participants || []).map(p => p.horse);

  /* Remove participants horses from available horses */
  if (removeParticipantsHorses && participantsHorsesUuid.length) {
    participantsHorsesUuid.map(uuid => {
      const index = availablesHorses.findIndex(horse => horse.uuid === uuid);
      if (index !== -1) {
        indexToRemove.push(index);
      }
    });
  }

  // check if the horse is available based on entree/sortie
  availablesHorses.forEach((horse, index) => {
    const horseAvailable = isHorseAvailable(horse, true);

    if (!horseAvailable) {
      indexToRemove.push(index);
    }
  });

  // remove all unavailable horse
  indexToRemove = indexToRemove.sort((a, b) => (a < b ? -1 : 1));
  for (let i = indexToRemove.length - 1; i >= 0; i -= 1) {
    availablesHorses.splice(indexToRemove[i], 1);
  }

  return availablesHorses;
}

function buildFilter (filterValues, filterField, condition, session) {
  const filters = [];

  filterValues.map(f => {
    filters.push(`"${session[filterField]}"${condition}"${f}"`);
  });

  return filters.join(' || ');
}

function filteredSessionPerContentType (contentType, filter, filterValues = []) {
  const { contentTypeAllSessions } = store.state;

  const filteredSessions = sessionsPerContentType(contentType, 'horses', contentTypeAllSessions);

  return (filteredSessions || []).filter(session => {
    const fil = buildFilter(filterValues, filter, '===', session);
    return eval(`${fil}`);
  });
}

function deleteHorseMovementsFields (horseMovement) {
  delete horseMovement.wtype;
  delete horseMovement.path;
  delete horseMovement.workspace;
  delete horseMovement.status;
  delete horseMovement.online;
  delete horseMovement.publishedAt;
  delete horseMovement.startPublicationDate;
  delete horseMovement.endPublicationDate;
  delete horseMovement.createTime;
  delete horseMovement.createUserName;
  delete horseMovement.createUserEmail;
  delete horseMovement.lastUpdateTime;
  delete horseMovement.lastUpdateUserName;
  delete horseMovement.lastUpdateUserEmail;
  delete horseMovement.previousETag;
  delete horseMovement.eTag;
  delete horseMovement.version;

  return horseMovement;
}

function deleteHorseFields (horse) {
  // delete fields
  delete horse.wtype;
  delete horse.publishedAt;
  delete horse.lastUpdateTime;
  delete horse.lastUpdateUserName;
  delete horse.isFolder;
  delete horse.locale;
  delete horse.mode;
  delete horse.wtitle;
  delete horse.wpath;
  delete horse.objectID;
  delete horse.sessions;

  return horse;
}

function prepareHorse (horse, horseMovementArray, deleteAuxFields = false) {
  let auxHorse = { ...horse };

  if (deleteAuxFields) {
    auxHorse = deleteHorseFields(horse);
  }

  return {
    ...auxHorse,
    horse_type: store.getters.referenceKey('equestrianType', auxHorse.horse_type),
    horse_race: store.getters.referenceKey('equestrianRace', auxHorse.horse_race),
    horse_dress: store.getters.referenceKey('equestrianDress', auxHorse.horse_dress),
    horse_gender: store.getters.referenceKey('equestrianGender', auxHorse.horse_gender),
    horse_group: store.getters.referenceKey('equestrianGroup', auxHorse.horse_group),
    horse_value: store.getters.referenceKey('equestrianValue', auxHorse.horse_value),
    horse_category: store.getters.referenceKey('equestrianCategory', auxHorse.horse_category),
    horse_birth_date: format(new Date(auxHorse.horse_birth_date * 1000), 'yyyy-MM-dd'),
    horse_date_entered: format(new Date(auxHorse.horse_date_entered * 1000), 'yyyy-MM-dd'),
    horse_movements: horseMovementArray
  };
}

function updateHorseOnStore (horse, updatedFields, text) {
  if (isObjectEmpty(updatedFields)) {
    return;
  }
  const { horses } = store.state;
  const index = horses.findIndex(h => h.uuid === horse.uuid);

  if (index !== -1) {
    for (const [key, value] of Object.entries(updatedFields)) {
      if (horses[index][key] !== value) {
        horses[index][key] = value;
      }
    }
  }
  sendNotification('success', text);
}

function computeInterval (sessionType = null) {
  switch (sessionType) {
    case 'GOLF':
      return { interval: 10, startMinute: 0, endMinute: 50 };
    default:
      return { interval: 15, startMinute: 0, endMinute: 45 };
  }
}

function startTimeOptions (sessionType = null) {
  const { selectedDate } = store.getters;
  const { interval, startMinute, endMinute } = computeInterval(sessionType);

  const hours = [];
  const start = 8;
  const end = 23;

  // comput hours from 8h to 23 per 15 minutes (ex: 08:00, 08:15, 08:30 ...)
  for (let hour = start; hour <= end; hour += 1) {
    const date = new Date(`${selectedDate}T${hour.toString().padStart(2, '0')}:00`);

    for (let minute = startMinute; minute <= endMinute; minute += interval) {
      const formated = format(add(date, { minutes: minute }), 'HH:mm');
      hours.push({ label: formated, value: formated });
    }
  }

  return hours;
}

function endTimeOptions () {
  const { selectedDate } = store.getters;
  const hours = [];
  const start = 8;
  const end = 24;

  // comput hours from 9h to 23 per 15 minutes (ex: 08:00, 08:15, 08:30 ...)
  for (let hour = start; hour < end; hour += 1) {
    const date = new Date(`${selectedDate}T${hour.toString().padStart(2, '0')}:00`);

    const startMinute = 0;
    const endMinute = 45;

    for (let minute = startMinute; minute <= endMinute; minute += 15) {
      const formated = format(add(date, { minutes: minute }), 'HH:mm');
      hours.push({ label: formated, value: formated });
    }
  }

  // add midnight to the array
  hours.push({ label: '00:00', value: '00:00' });

  return hours;
}

function typeToFrench (type) {
  switch (type) {
    case 'horse':
      return 'cheval';
    case 'place':
      return 'lieu';
    case 'customer':
      return 'client';
    case 'instructor':
      return 'moniteur';
    default:
      return '';
  }
}

function groupContent (arrayToGroup, groupBy) {
  const group = {};
  arrayToGroup.map(item => {
    if (!group[item[groupBy]]) {
      group[item[groupBy]] = [];
    }
    group[item[groupBy]].push(item);
  });
  return group;
}

// function nestedGrouping (arrayToGroup, groupByProperties) {
//   const properties = Array.from(groupByProperties);
//   if (properties.length === 1) {
//     return groupContent(arrayToGroup, properties[0]);
//   }

//   const property = properties.shift();
//   const grouped = groupContent(arrayToGroup, property);

//   for (const key in grouped) {
//     grouped[key] = nestedGrouping(grouped[key], Array.from(properties));
//   }
//   return grouped;
// }

function sortInsideGroup (groupedContent, sortBy) {
  const aux = [];

  for (const key of Object.keys(groupedContent)) {
    groupedContent[key] = sortArray(groupedContent[key], sortBy);
    groupedContent[key].map(content => aux.push(content));
  }

  return aux;
}

// function sortNestedGroup (group, sortByProperty, auxNestedSorting = []) {
//   const currentGroup = { ...group };
//   for (const key of Object.keys(currentGroup)) {
//     if (typeof currentGroup[key] === 'object' && !Array.isArray(currentGroup[key])) {
//       currentGroup[key] = sortNestedGroup(currentGroup[key], sortByProperty, auxNestedSorting);
//     } else if (Array.isArray(currentGroup[key])) {
//       currentGroup[key] = sortArray(currentGroup[key], sortByProperty);
//       currentGroup[key].forEach(content => auxNestedSorting.push(content));
//     } else {
//       console.log('[ERROR]: Data type not supported: ', typeof currentGroup[key] === 'object');
//     }
//   }
//   return auxNestedSorting;
// }

function contentWithWithoutSession (wtype) {
  const sortableContent = { horse: 'horse_name' };
  const contentTypes = {
    with_session: [],
    without_session: []
  };

  let indexToRemove = [];

  const contents = store.state[`${wtype}s`];
  const sessions = store.getters.dailySessions;

  contents.map(contentType => {
    if (contentType.owners && contentType.owners.length) {
      return;
    }

    const sessionField = getSessionFieldByWtype(contentType.wtype);

    const contentTypeSessions = sessionsPerContentType(contentType, sessionField, sessions);

    if (contentTypeSessions && contentTypeSessions.length) {
      contentTypeSessions.map(session => {
        const alreadOnBag = contentTypes.with_session.some(s => s.uuid === contentType.uuid);
        if (!alreadOnBag) {
          contentTypes.with_session.push({ ...contentType, sessionId: session.id, sessionName: session.name, sessionStartTime: session.start_time });

          // verify if is on without_session
          const index = contentTypes.without_session.findIndex(c => c.uuid === contentType.uuid);
          if (index !== -1) {
            indexToRemove.push(index);
          }
        }
      });

      return;
    }
    contentTypes.without_session.push(contentType);
  });

  if (sortableContent.hasOwnProperty(wtype)) {
    const group = groupContent(contentTypes.with_session, ['sessionId']);
    contentTypes.with_session = sortInsideGroup(group, 'horse_name');
  }

  if (indexToRemove && indexToRemove.length) {
    indexToRemove = indexToRemove.sort((a, b) => (a < b ? -1 : 1));
    for (let i = indexToRemove.length - 1; i >= 0; i -= 1) {
      contentTypes.without_session.splice(indexToRemove[i], 1);
    }
  }

  return contentTypes;
}

function separateContentTypesWithAndWithoutSession (wtype) {
  if (wtype !== 'customer') {
    return contentWithWithoutSession(wtype);
  }
  const contentTypes = {
    with_session: [],
    without_session: []
  };

  let sessions = store.getters.dailySessions;

  sessions = sortArray(sessions, 'start_time');

  const { filteredSessions } = computeSessionMap(sessions, true);

  const { isSessionFilterApplied } = store.state;

  sessions.map(s => {
    (s.participants || []).map(participant => {
      const alreadyOnSession = contentTypes.with_session.map(c => c.uuid).includes(participant.uuid);

      if (!alreadyOnSession) {
        contentTypes.with_session.push({
          ...participant,
          wtype: 'customer',
          fullname: `${participant.firstname} ${participant.lastname}`,
          sessionId: s.id
        });
      }
    });
  });

  const { selectedCustomers } = store.state;

  selectedCustomers.map(customer => {
    if (!contentTypes.with_session.map(c => c.uuid).includes(customer.uuid)) {
      contentTypes.without_session.push(customer);
    }
  });
  let customersWithSession = contentTypes.with_session;

  const group = groupContent(customersWithSession, 'sessionId');
  customersWithSession = sortInsideGroup(group, 'lastname');

  let customersWithoutSession = sortArray(contentTypes.without_session, 'lastname');

  customersWithSession = customersWithSession.filter(customer => (isSessionFilterApplied ? filteredSessions.some(s => (s.participants || []).some(p => p.uuid === customer.uuid)) : true));

  customersWithoutSession = customersWithoutSession.filter(customer => (isSessionFilterApplied ? filteredSessions.some(s => (s.participants || []).some(p => p.uuid === customer.uuid)) : true));

  return {
    with_session: customersWithSession,
    without_session: customersWithoutSession
  };
}

function filterHorse (horses, showHorseWithoutRider, session, selectedHorseTypes, riderTypeExclusion = null, levelFilter = null, ignoreOverlap = false) {
  if (!ignoreOverlap) {
    const horsesBusyInInterval = busyContentTypes('horses', session);
    horses = horses.filter(h => !horsesBusyInInterval.some(horseInterval => horseInterval.uuid === h.uuid));
  }

  /* If we want to exclude horses that belong to the instructors or participants tab */
  if (riderTypeExclusion) {
    horses = horses.filter(h => {
      const index = session.horses.findIndex(horse => horse.uuid === h.uuid);
      if (index !== -1) {
        return riderTypeExclusion === 'instructors' ? !session.horses[index].isInstructorHorse : session.horses[index].isInstructorHorse;
      }
      return true;
    });
  }

  if (levelFilter && levelFilter.length) {
    horses = horses.filter(h => (h.level ? levelFilter.includes(h.level.uuid) : false));
  }

  return horses.map(horse => computeDailyWeeklyHorseEffort(horse)).filter(h => {
    if (showHorseWithoutRider) {
      return (session.horses || []).some(horse => horse.uuid === h.uuid) && !(session.participants || []).some(p => p.horse === h.uuid);
    }

    if (selectedHorseTypes && selectedHorseTypes.length) {
      if (selectedHorseTypes.includes('ALL')) {
        return true;
      }
      return selectedHorseTypes.includes(h.horse_type);
    }

    return true;
  });
}

const checkHorseDailyEffort = async (horses, sessions, excludeSessionIds = []) => {
  const { selectedDate } = store.getters;

  let horsesAlertEffort = {};

  const { sessionsThisWeek, sessionsNotThisWeek } = separateSessionsByWeek(sessions, selectedDate);

  if (Object.keys(sessionsThisWeek)?.length) {
    horses.forEach(horse => {
      Object.keys(sessionsThisWeek).forEach(day => {
        const dailySessionsToAdd = sessionsThisWeek[day];

        let dailySessions = store.getters.dailySessionsByContentType(horse);

        dailySessions = [...dailySessions, ...dailySessionsToAdd];

        if (excludeSessionIds.length) {
          dailySessions = dailySessions.filter(s => !excludeSessionIds.includes(s.id));
        }

        const dailyEffort = minutesToHours(computeEffort(dailySessions, horse));
        if (parseFloat(dailyEffort) > parseFloat(horse.maxHoursEffort)) {
          horsesAlertEffort = computeEffortAlert(horsesAlertEffort, horse, day, dailyEffort, dailySessionsToAdd);
        }
      });
    });
  }

  if (Object.keys(sessionsNotThisWeek)?.length) {
    const daysToQuery = [];
    Object.keys(sessionsNotThisWeek).forEach(day => {
      if (!daysToQuery.includes(day)) {
        daysToQuery.push(day);
      }
    });

    try {
      const horsesUuids = (horses || []).map(h => h.uuid);
      const fetchedSessions = await openSearchCustomSessions(buildDailySessionsQuery(daysToQuery, horsesUuids));

      if (!fetchedSessions.length) {
        return horsesAlertEffort;
      }

      horses.forEach(horse => {
        daysToQuery.forEach(dayStart => {
          let horsesDailySessions = fetchedSessions.filter(session => {
            if (!session.horses.some(h => h.uuid === horse.uuid)) {
              return false;
            }
            if (isSameDay(fromUnixTime(session.start_time), parseISO(dayStart))) {
              return true;
            }
            return false;
          });

          horsesDailySessions = [...horsesDailySessions, ...sessionsNotThisWeek[dayStart]];

          const dailyEffort = minutesToHours(computeEffort(horsesDailySessions, horse));

          if (parseFloat(dailyEffort) > parseFloat(horse.maxHoursEffort)) {
            horsesAlertEffort = computeEffortAlert(horsesAlertEffort, horse, dayStart, dailyEffort, sessionsNotThisWeek[dayStart]);
          }
        });
      });
    } catch (error) {
      console.error('Error fetching sessions from past/future weeks:', error);
    }
  }

  return horsesAlertEffort;
};

export {
  busyContentTypes,
  sessionTypesAllowInsertEffort,
  filterHorse,
  computeEffort,
  separateContentTypesWithAndWithoutSession,
  typeToFrench,
  isHorseAvailable,
  isContentTypeAvailable,
  generateUuid,
  availableContentType,
  startTimeOptions,
  endTimeOptions,
  deleteHorseMovementsFields,
  updateHorseOnStore,
  prepareHorse,
  deleteHorseFields,
  filteredSessionPerContentType,
  availableHorses,
  sortInsideGroup,
  groupContent,
  checkHorseDailyEffort,
  computeDailyWeeklyHorseEffort
};
